/// <reference path="ReconciliationEntryState.ts" />
/// <reference path="InvoiceSectionReconciliationHeader.ts" />
/// <reference path="InvoiceLineReconciliationCell.ts" />

interface LedgerAccountReconciliationDataSourceDelegate
{
    /// The item to show in the panel; for an invoice, with the lines the list shows of it.
    didSelectItem(item:CoreAccounting, lines?:CoreAccounting[]):void;
    /// A ledger account or classification of the item (or of its lines) was changed in the list.
    itemAccountsDidChange(item:CoreAccounting):void;
}

// The invoices of the reconciliation list, fetched through their lines: a section per invoice
// with its head (a copy of the header template) and its lines, each invoice drawn as a card. The
// sales and purchase subclasses give the entity of the lines and what to prefetch with them.
class InvoiceLineReconciliationDataSource extends MIOObject implements InvoiceSectionReconciliationHeaderDelegate, InvoiceLineReconciliationCellDelegate
{
    protected tableView:UITableView = null;
    protected delegate:LedgerAccountReconciliationDataSourceDelegate = null;
    private headerTemplate:InvoiceSectionReconciliationHeader = null;

    initWithTableView(tableView:UITableView, headerTemplate:InvoiceSectionReconciliationHeader, delegate:LedgerAccountReconciliationDataSourceDelegate) {
        this.tableView = tableView;
        this.headerTemplate = headerTemplate;
        this.delegate = delegate;
    }

    /// The entity of the lines.
    protected lineEntityName() : string { return null; }

    /// What the heads and lines show, fetched with the lines.
    protected prefetchKeyPaths() : string[] { return []; }

    private fromDate:string = null;
    private toDate:string = null;
    private statusFilter:ReconciliationStatusFilter = ReconciliationStatusFilter.all;
    private filterFormat:string = null;

    private selectedDocument:Invoice = null;
    private headers:InvoiceSectionReconciliationHeader[] = [];

    reloadData(fromDate:string, toDate:string, statusFilter:ReconciliationStatusFilter, filterFormat:string) {
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.statusFilter = statusFilter;
        this.filterFormat = filterFormat;
        this.selectedDocument = null;

        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        this.fetchedResultsController = null;
        this.reloadTable(false);
    }

    private reloadTable(keepScrollPosition:boolean) {
        let scrollTop = this.tableView.layer.scrollTop;
        this.headers = [];
        this.tableView.reloadData();
        if (keepScrollPosition) this.tableView.layer.scrollTop = scrollTop;
    }

    numberOfSections(tableView:UITableView) {
        return this.fetchedResultsController?.sections.length ?? 0;
    }

    numberOfRowsInSection(tableView:UITableView, section:number) {
        let sec = this.fetchedResultsController.sections[section];
        return sec.numberOfObjects();
    }

    viewForHeaderInSection(tableView:UITableView, section:number) {
        let lines = this.linesInSection(section);
        let document = lines[0]?.document as Invoice;

        let header = this.headerTemplate.copy() as InvoiceSectionReconciliationHeader;
        header.awakeFromHTML();
        header.hidden = false;
        header.delegate = this;
        header.setDocument(document, lines);
        header.selected = document != null && document == this.selectedDocument;

        this.headers[section] = header;
        return header;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let line = this.fetchedResultsController.objectAtIndexPath(indexPath) as InvoiceLine;

        let cell = tableView.dequeueReusableCellWithIdentifier("InvoiceLineCell") as InvoiceLineReconciliationCell;
        cell.delegate = this;
        cell.documentState = this.stateForSection(indexPath.section);
        cell.item = line;
        cell.isLastInSection = indexPath.row == this.numberOfRowsInSection(tableView, indexPath.section) - 1;

        return cell;
    }

    // a click on a line selects its invoice
    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let line = this.fetchedResultsController.objectAtIndexPath(indexPath) as InvoiceLine;
        this.selectDocument(line?.document as Invoice);
    }

    private selectDocument(document:Invoice) {
        if (document == null || document == this.selectedDocument) return;
        this.selectedDocument = document;
        for (let header of this.headers) {
            if (header != null) header.selected = header.document == document;
        }
        let section = this.sectionForDocument(document);
        this.delegate?.didSelectItem(document, section >= 0 ? this.linesInSection(section) : []);
    }

    headerDidSelectDocument(header:InvoiceSectionReconciliationHeader, document:Invoice) {
        this.selectDocument(document);
    }

    // the lines inheriting the invoice's default account show the new one
    headerDidChangeAccounts(header:InvoiceSectionReconciliationHeader, document:Invoice) {
        this.refreshItem(document);
        this.accountsDidChange(document);
    }

    // the head shows the classification the lines share; when the account went to the product or
    // concept item, its other lines on screen show what they inherit now
    lineDidChangeAccounts(cell:InvoiceLineReconciliationCell, line:InvoiceLine, item?:CoreItem) {
        let document = line.document as Invoice;
        let section = this.sectionForDocument(document);
        if (section >= 0) this.headers[section]?.update();
        if (item != null) this.refreshLinesOfItem(item, cell);
        this.accountsDidChange(document);
    }

    // every line of the item drawn on screen, whatever its invoice
    private refreshLinesOfItem(item:CoreItem, except:InvoiceLineReconciliationCell) {
        let sections = this._fetchedResultsController?.sections ?? [];
        for (let section = 0; section < sections.length; section++) {
            let rows = this.numberOfRowsInSection(this.tableView, section);
            for (let row = 0; row < rows; row++) {
                let cell = this.tableView.cellAtIndexPath(MIOIndexPath.indexForRowInSection(row, section)) as InvoiceLineReconciliationCell;
                if (cell == null || cell == except) continue;
                let line = cell.item;
                if (line?.product == item || line?.conceptItem == item) cell.update();
            }
        }
    }

    // the panel shows the invoice whose entry the change is for
    private accountsDidChange(document:Invoice) {
        if (document == null) return;
        this.selectDocument(document);
        this.delegate?.itemAccountsDidChange(document);
    }

    /// The head and the lines of the invoice show its current state (after its entry changed).
    refreshItem(item:CoreAccounting) {
        let section = this.sectionForDocument(item);
        if (section < 0) return;

        this.headers[section]?.update();

        let state = this.stateForSection(section);
        let rows = this.numberOfRowsInSection(this.tableView, section);
        for (let row = 0; row < rows; row++) {
            let cell = this.tableView.cellAtIndexPath(MIOIndexPath.indexForRowInSection(row, section)) as InvoiceLineReconciliationCell;
            if (cell == null) continue;
            cell.documentState = state;
            cell.update();
        }
    }

    // the invoice's state, counting the lines booked one by one
    private stateForSection(section:number) : ReconciliationEntryState {
        let lines = this.linesInSection(section);
        return ReconciliationEntryStateHelper.stateForDocument(lines[0]?.document, lines);
    }

    private sectionForDocument(document:CoreAccounting) : number {
        if (document == null) return -1;
        let sections = this._fetchedResultsController?.sections ?? [];
        for (let index = 0; index < sections.length; index++) {
            let first = sections[index].objects[0] as InvoiceLine;
            if (first?.document == document) return index;
        }
        return -1;
    }

    private linesInSection(section:number) : InvoiceLine[] {
        return (this.fetchedResultsController.sections[section]?.objects ?? []) as InvoiceLine[];
    }

    private _fetchedResultsController:MIOFetchedResultsController = null;
    set fetchedResultsController(value:MIOFetchedResultsController) {
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;

        this._fetchedResultsController = value;
    }

    get fetchedResultsController():MIOFetchedResultsController {
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let conditions = [];
        if (this.fromDate != null) {
            conditions.push("document.date >= '" + this.fromDate + "' AND document.date <= '" + this.toDate + "'");
        }
        if (this.filterFormat != null) conditions.push("(" + this.filterFormat + ")");

        let statusFormat = ReconciliationEntryStateHelper.predicateFormatForFilter(this.statusFilter, "document.");
        if (statusFormat != null) conditions.push("(" + statusFormat + ")");

        let pf = conditions.length > 0 ? conditions.join(" AND ") : null;

        // the invoices newest first, the lines of each one together and in their order
        let sds = [
            MIOSortDescriptor.sortDescriptorWithKey("document.date", false),
            MIOSortDescriptor.sortDescriptorWithKey("document.documentNumber", false),
            MIOSortDescriptor.sortDescriptorWithKey("orderIndex", true)
        ];
        let fetchRequest = DBHelper.listFetchRequestWithEntityName(this.lineEntityName(), sds, pf);
        fetchRequest.relationshipKeyPathsForPrefetching = this.prefetchKeyPaths();

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, "document.identifier");
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();
        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    // a saved line rebuilds the list: the selection and the scroll position stay
    controllerDidChangeContent(controller:MIOFetchedResultsController) {
        this.reloadTable(true);
    }
}
